import { nonEmptyString } from "../app/media";

function popupStorage(config) {
  try {
    if (config.frequency === "session") return window.sessionStorage;
    if (config.frequency === "browser") return window.localStorage;
  } catch {
    return null;
  }

  return null;
}

export function initNewsletterDialog(campaignConfig) {
  const dialog = document.querySelector("[data-newsletter-dialog]");
  const panel = dialog?.querySelector("[data-newsletter-dialog-panel]");
  const closeButton = dialog?.querySelector("[data-newsletter-dialog-close]");
  const config = campaignConfig.popup ?? {};

  if (
    !dialog
    || !panel
    || !closeButton
    || !config.enabled
    || typeof dialog.showModal !== "function"
  ) return;

  let returnFocusTo = null;
  const storage = popupStorage(config);
  const storageKey = nonEmptyString(config.storageKey)
    ? config.storageKey
    : "suchard-newsletter-popup";

  const wasShown = () => {
    if (config.frequency === "always" || !storage) return false;
    try {
      return storage.getItem(storageKey) === "shown";
    } catch {
      return false;
    }
  };

  const rememberShown = () => {
    if (config.frequency === "always" || !storage) return;
    try {
      storage.setItem(storageKey, "shown");
    } catch {
      // Storage may be disabled without preventing the dialog from working.
    }
  };

  const restorePage = () => {
    document.body.classList.remove("dialog-is-open");
    returnFocusTo?.focus?.();
    returnFocusTo = null;
  };

  const closeDialog = () => {
    if (dialog.open) dialog.close();
  };

  const openDialog = ({ source = null, automatic = false } = {}) => {
    if (dialog.open || (automatic && wasShown())) return false;

    returnFocusTo = source instanceof HTMLElement && source !== document.body
      ? source
      : null;
    dialog.showModal();
    document.body.classList.add("dialog-is-open");
    rememberShown();
    dialog.focus({ preventScroll: true });
    return true;
  };

  dialog.addEventListener("close", restorePage);
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeDialog();
  });

  dialog.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;

    const focusableElements = Array.from(
      dialog.querySelectorAll("button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"),
    ).filter((element) => !element.hidden);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);
    const focusStartsOnDialog = document.activeElement === dialog;

    if (event.shiftKey && (focusStartsOnDialog || document.activeElement === firstElement)) {
      event.preventDefault();
      lastElement?.focus();
    } else if (!event.shiftKey && (focusStartsOnDialog || document.activeElement === lastElement)) {
      event.preventDefault();
      firstElement?.focus();
    }
  });

  closeButton.addEventListener("click", closeDialog);
  dialog.addEventListener("click", (event) => {
    if (!panel.contains(event.target)) closeDialog();
  });

  document.querySelectorAll("[data-newsletter-dialog-open]").forEach((control) => {
    control.addEventListener("click", (event) => {
      event.preventDefault();
      openDialog({ source: control });
    });
  });

  window.addEventListener("suchard:open-newsletter", () => {
    openDialog({ source: document.activeElement });
  });

  if (wasShown()) return;

  if (config.trigger === "delay") {
    const delay = Number.isFinite(config.delayMs) ? Math.max(0, config.delayMs) : 0;
    window.setTimeout(() => openDialog({ automatic: true }), delay);
    return;
  }

  if (config.trigger === "scroll") {
    const threshold = Number.isFinite(config.scrollThreshold)
      ? Math.min(1, Math.max(0, config.scrollThreshold))
      : 0.2;
    const openAtThreshold = () => {
      const scrollable = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        1,
      );
      if (window.scrollY / scrollable < threshold) return;

      window.removeEventListener("scroll", openAtThreshold);
      openDialog({ automatic: true });
    };

    window.addEventListener("scroll", openAtThreshold, { passive: true });
    openAtThreshold();
    return;
  }

  if (config.trigger === "load") openDialog({ automatic: true });
}
